"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarCheck, ShieldCheck, Clock, FileCheck2, HardHat, ArrowRight, SunMedium, FolderKanban, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function Partnerships() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = [{
    id: "b-1",
    icon: CalendarCheck,
    title: "Priority booking & flexible call-offs",
    description: "Dedicated scheduling slots protect your programme timings. When site phases shift or weather delays hit, your crew moves without penalty fees or friction."
  }, {
    id: "b-2",
    icon: ShieldCheck,
    title: "Full safety & compliance packs",
    description: "Every structure meets TG20:21 standards with site-specific risk assessments, method statements, and hand-over certificates provided before work starts."
  }, {
    id: "b-3",
    icon: Clock,
    title: "Predictable commercial pricing",
    description: "Clear schedule-of-rates agreements eliminate guesswork. No surprise hire fees, transparent adaptation terms, and prompt invoicing built for contractor cash flow."
  }, {
    id: "b-4",
    icon: HardHat,
    title: "Direct site supervisor accountability",
    description: "A single point of contact oversees your projects across Berkshire, Surrey, Hampshire, and London. Pete Wood and our senior supervisors remain reachable daily."
  }];
  const contractorProfiles = [{
    id: "cp-1",
    category: "Solar PV Installers",
    detail: "Purpose-engineered roof access, handrails, and edge protection aligned with panel arrays for smooth solar installation and zero tile damage.",
    linkTo: "/SolarScaffoldingPage",
    linkLabel: "View solar access details"
  }, {
    id: "cp-2",
    category: "Roofing & Main Contractors",
    detail: "Complete perimeter scaffolding, loading bays, and temporary roofs configured for multi-week re-roofing and refurbishment contracts.",
    linkTo: "/ProjectsCaseStudiesPage",
    linkLabel: "Browse contractor projects"
  }];
  const accreditations = ["NASC Member Contractor", "CHAS Accredited", "20+ Years Trade Experience"];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="longtermrelationshipbenefits" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <Badge variant="outline" className="mb-4 border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground">
            Contractor Partnerships
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground mb-6">
            Reliable scaffolding partnerships built for repeat contractors.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding Ltd supports solar installers, roofing teams, and main contractors across Berkshire, Surrey, Hampshire, Buckinghamshire, and London with reliable programmes and transparent terms.
          </p>
        </div>

        {/* Core Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {benefits.map((benefit, index) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.id} variants={itemVariants} data-index={index} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="flex flex-row items-start gap-4 space-y-0 pb-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
                      <IconComponent className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold font-default text-card-foreground">
                        {benefit.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Partnership Focus Areas */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          {contractorProfiles.map((profile, index) => <div key={profile.id} data-index={index} className="lg:col-span-6 bg-card border border-border rounded-xl p-8 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {profile.id === "cp-1" ? <SunMedium className="h-5 w-5 text-primary" /> : <FolderKanban className="h-5 w-5 text-primary" />}
                  <h3 className="text-xl font-semibold font-default text-card-foreground">
                    {profile.category}
                  </h3>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                  {profile.detail}
                </p>
              </div>
              <div>
                <Button asChild variant="outline" className="w-full sm:w-auto font-medium">
                  <Link to={profile.linkTo} className="flex items-center justify-center gap-2">
                    <span>{profile.linkLabel}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>)}
        </div>

        {/* Accreditation Strip & Action Box */}
        <div className="bg-secondary text-secondary-foreground rounded-2xl p-8 sm:p-10 lg:p-12 shadow-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap gap-3 mb-6">
                {accreditations.map((item, index) => <div key={item} data-index={index} className="inline-flex items-center gap-1.5 bg-secondary-foreground/10 text-secondary-foreground text-xs font-medium px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary-foreground" />
                    <span>{item}</span>
                  </div>)}
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-secondary-foreground mb-3">
                Ready to secure an ongoing access partner?
              </h3>
              <p className="text-secondary-foreground/80 text-sm sm:text-base max-w-xl leading-relaxed">
                Speak directly with our commercial team to discuss schedule agreements, master subcontracts, or tender pricing for your upcoming works.
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4 justify-end">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage" className="flex items-center justify-center gap-2">
                  <span>Discuss a Solar Partnership</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-secondary-foreground/10 border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/20 font-medium w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage">
                  Request a Contractor Quote
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}